import { Button } from "@designs-by-maryam/ui/components/button";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Award, CheckCircle, Clock } from "lucide-react";
import { motion } from "motion/react";
import { getStaggerDelay, useSafeMotion } from "@/lib/motion";

type StatItem = {
	value: string;
	label: string;
};

type HeroSectionProps = {
	/**
	 * Professional headshot image source
	 */
	photoSrc?: string;
	/**
	 * Alt text for the photo (accessibility)
	 */
	photoAlt?: string;
	/**
	 * Main headline text
	 */
	headline?: string;
	/**
	 * Subtitle text
	 */
	subtitle?: string;
	/**
	 * Brief bio text (2-3 sentences)
	 */
	bio?: string;
	/**
	 * Display CRPEP certification badge
	 */
	showCRPEPBadge?: boolean;
	/**
	 * Stats array from CMS (optional - falls back to defaults)
	 */
	stats?: StatItem[];
	/**
	 * Primary CTA button text
	 */
	ctaText?: string;
	/**
	 * Primary CTA button link
	 */
	ctaLink?: string;
	/**
	 * Secondary CTA button text
	 */
	secondaryCtaText?: string;
	/**
	 * Secondary CTA button link
	 */
	secondaryCtaLink?: string;
	/**
	 * Additional className for customization
	 */
	className?: string;
};

const DEFAULT_STATS: StatItem[] = [
	{ value: "50+", label: "Projects Completed" },
	{ value: "5+", label: "Years Experience" },
	{ value: "100%", label: "Client Satisfaction" },
];

// Map stat labels to icons for visual consistency
function getStatIcon(label: string) {
	const lowerLabel = label.toLowerCase();
	if (lowerLabel.includes("project") || lowerLabel.includes("completed")) {
		return Award;
	}
	if (lowerLabel.includes("year") || lowerLabel.includes("experience")) {
		return Clock;
	}
	if (
		lowerLabel.includes("satisfaction") ||
		lowerLabel.includes("client") ||
		lowerLabel.includes("100")
	) {
		return CheckCircle;
	}
	return Award; // Default fallback
}

/**
 * HeroSection Component
 *
 * Displays the homepage hero section with professional photo, headline,
 * CRPEP certification badge, bio, and call-to-action buttons.
 *
 * Features:
 * - Responsive layout (side-by-side on desktop, stacked on mobile)
 * - Decorative background shape behind photo
 * - CRPEP badge displayed as pill above headline
 * - Accessible semantic HTML with proper heading hierarchy
 * - Keyboard navigation support
 */
export function HeroSection({
	photoSrc = "/design-refs/logo/maryam-profile.jpg",
	photoAlt = "Maryam - Interior Design Professional",
	headline = "Crafting Exceptional Interiors",
	subtitle = "Transforming Spaces with Elegant Interior Design",
	bio = "I'm an interior designer passionate about creating spaces that reflect the warmth and sophistication of Bahraini culture. With expertise in modern design principles and deep respect for regional aesthetics, I transform your vision into beautiful, functional living spaces.",
	showCRPEPBadge = true,
	stats,
	ctaText = "View Portfolio",
	ctaLink = "/portfolio",
	secondaryCtaText = "Book Consultation",
	secondaryCtaLink = "/contact",
	className,
}: HeroSectionProps) {
	// Use provided stats or fall back to defaults
	const displayStats = stats && stats.length > 0 ? stats : DEFAULT_STATS;
	const { shouldReduce, slideRight, slideLeft, slideUp, scaleIn } =
		useSafeMotion();
	const heroImage = (
		// biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError handles image fallback.
		<img
			alt={photoAlt}
			className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
			data-testid="hero-photo"
			height={500}
			loading="eager"
			onError={(e) => {
				e.currentTarget.style.display = "none";
			}}
			src={photoSrc}
			width={400}
		/>
	);

	return (
		<section
			aria-label="Hero section"
			className={cn(
				"relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background py-16 md:py-24",
				className,
			)}
			data-testid="hero-section"
		>
			{/* Enhanced Background Grid Pattern with gradient overlay */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.03]"
				style={{
					backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
					backgroundSize: "32px 32px",
				}}
			/>

			{/* Decorative corner shapes with richer blur - matching About page */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute -top-10 right-6 h-48 w-48 rounded-full bg-primary/8 blur-3xl"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute bottom-[-24px] left-0 h-56 w-56 rounded-full bg-purple-500/6 blur-3xl"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute top-16 left-12 h-32 w-32 rounded-full bg-primary/6 blur-2xl"
			/>

			<div className="container relative mx-auto px-4 md:px-6">
				<div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
					{/* Photo Section - Left on desktop, top on mobile */}
					<motion.div
						animate={slideRight.animate}
						className="relative mx-auto w-full max-w-[420px] lg:mx-0"
						initial={slideRight.initial}
						transition={slideRight.transition}
					>
						{/* Decorative Background Shape - upgraded border */}
						<motion.div
							animate={scaleIn.animate}
							aria-hidden="true"
							className="-translate-x-4 -z-10 sm:-translate-x-6 absolute inset-0 translate-y-4 rounded-tl-[80px] rounded-br-[40px] border-2 border-primary/20 bg-primary/12 shadow-xl sm:translate-y-6 lg:rounded-tl-[100px] lg:rounded-br-[50px] dark:bg-primary/15"
							initial={scaleIn.initial}
							transition={{
								duration: shouldReduce ? 0.01 : 0.8,
								delay: shouldReduce ? 0 : 0.2,
							}}
						/>

						{/* Main Image with Credential Overlay - upgraded borders and shadow */}
						<div className="group relative aspect-[4/5] overflow-hidden rounded-2xl border-2 border-primary/20 bg-card/80 shadow-2xl ring-1 ring-primary/15 transition-all duration-300 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]">
							{heroImage}

							{/* Credential Overlay - shows on hover */}
							<div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 backdrop-blur-[2px] transition-all duration-500 group-hover:opacity-100">
								<motion.div
									className="w-full p-6 text-white"
									initial={{ y: 20, opacity: 0 }}
									transition={{ duration: 0.3 }}
									whileHover={{ y: 0, opacity: 1 }}
								>
									<div className="mb-2 flex items-center gap-2">
										<Award className="h-5 w-5 text-gold" />
										<span className="font-semibold tracking-wide">
											CRPEP Licensed
										</span>
									</div>
									<p className="text-sm text-white/90">
										Category B Engineering License
									</p>
								</motion.div>
							</div>
						</div>

						{/* Decorative Accent Circle */}
						<div
							aria-hidden="true"
							className="-top-4 -right-4 absolute h-16 w-16 rounded-full bg-primary/10 blur-lg dark:bg-primary/5"
						/>
					</motion.div>

					{/* Content Section - Right on desktop, bottom on mobile */}
					<motion.div
						animate={slideLeft.animate}
						className="flex flex-col items-center gap-6 text-center lg:items-start lg:text-left"
						initial={slideLeft.initial}
						transition={{
							...slideLeft.transition,
							delay: shouldReduce ? 0 : 0.2,
						}}
					>
						{/* CRPEP Badge - Pill Style with shimmer effect */}
						{showCRPEPBadge && (
							<motion.div
								animate={{ opacity: 1, y: 0 }}
								className="group relative inline-flex items-center overflow-hidden rounded-full border-2 border-primary/30 bg-gradient-to-r from-primary/15 to-primary/5 px-4 py-1.5 font-semibold text-primary text-sm shadow-lg ring-1 ring-primary/20 dark:bg-gradient-to-r dark:from-primary/20 dark:to-primary/10 dark:ring-primary/30"
								data-testid="crpep-badge"
								initial={{ opacity: 0, y: shouldReduce ? 0 : -20 }}
								transition={{
									duration: shouldReduce ? 0.01 : 0.5,
									delay: shouldReduce ? 0 : 0.4,
								}}
							>
								{/* Shimmer effect */}
								<span
									aria-hidden="true"
									className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-800 group-hover:translate-x-full motion-reduce:transition-none"
								/>
								<span
									aria-hidden="true"
									className="relative mr-2 h-1.5 w-1.5 animate-pulse rounded-full bg-primary motion-reduce:animate-none"
								/>
								<span className="relative">
									CRPEP Licensed Interior Architect
								</span>
							</motion.div>
						)}

						{/* Headlines with enhanced typography */}
						<div className="space-y-4">
							<motion.h1
								animate={slideUp.animate}
								className="bg-gradient-to-br from-foreground via-foreground to-foreground/70 bg-clip-text font-bold text-4xl text-transparent tracking-tight md:text-5xl lg:text-6xl xl:text-7xl"
								data-testid="hero-headline"
								initial={slideUp.initial}
								style={{ letterSpacing: "-0.02em" }}
								transition={{
									...slideUp.transition,
									delay: shouldReduce ? 0 : 0.5,
								}}
							>
								{headline}
							</motion.h1>
							<motion.p
								animate={slideUp.animate}
								className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text font-semibold text-transparent text-xl tracking-wide md:text-2xl lg:text-3xl"
								data-testid="hero-subtitle"
								initial={slideUp.initial}
								transition={{
									...slideUp.transition,
									delay: shouldReduce ? 0 : 0.6,
								}}
							>
								{subtitle}
							</motion.p>
						</div>

						{/* Bio */}
						<motion.p
							animate={{ opacity: 1 }}
							className="max-w-[600px] text-lg text-muted-foreground leading-relaxed"
							data-testid="hero-bio"
							initial={{ opacity: 0 }}
							transition={{
								duration: shouldReduce ? 0.01 : 0.5,
								delay: shouldReduce ? 0 : 0.7,
							}}
						>
							{bio}
						</motion.p>

						{/* Stats Section - Compact inline style */}
						<motion.div
							animate={slideUp.animate}
							className="flex w-full flex-wrap justify-center gap-6 border-border/30 border-t pt-6 lg:justify-start"
							initial={slideUp.initial}
							transition={{
								...slideUp.transition,
								delay: shouldReduce ? 0 : 0.8,
							}}
						>
							{displayStats.map((stat, index) => {
								const StatIcon = getStatIcon(stat.label);
								return (
									<motion.div
										animate={scaleIn.animate}
										className="group flex items-center gap-2"
										initial={scaleIn.initial}
										key={stat.label}
										transition={{
											duration: shouldReduce ? 0.01 : 0.3,
											delay: getStaggerDelay(index, 0.9, 0.1, shouldReduce),
										}}
									>
										<StatIcon className="h-4 w-4 text-primary" />
										<span className="font-semibold text-foreground">
											{stat.value}
										</span>
										<span className="text-muted-foreground text-sm">
											{stat.label}
										</span>
									</motion.div>
								);
							})}
						</motion.div>

						{/* Call-to-Action Buttons */}
						<motion.div
							animate={slideUp.animate}
							className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row"
							initial={slideUp.initial}
							transition={{
								...slideUp.transition,
								delay: shouldReduce ? 0 : 1.0,
							}}
						>
							<Button
								asChild
								className="w-full sm:w-auto"
								data-testid="cta-portfolio"
								size="lg"
								variant="brand"
							>
								<Link to={ctaLink}>
									{ctaText}
									<ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
								</Link>
							</Button>
							<Button
								asChild
								className="w-full sm:w-auto"
								data-testid="cta-consultation"
								size="lg"
								variant="brand-outline"
							>
								<Link to={secondaryCtaLink}>{secondaryCtaText}</Link>
							</Button>
						</motion.div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
